import { apiv1 as api } from '@/adapters';
import type {
  CredentialRequirement,
  FeatureModel,
  ManifestModel,
  ProviderModel,
} from '@/adapters/apiv1.g';
import {
  extractAccepted,
  extractBoolean,
  extractConnectorManifest,
  extractPageModels,
  extractProvider,
  extractProviderFeature,
  unwrapData,
} from '@/adapters/shared';
import type { Feature, Provider } from '@/types/core/provider';
import type { ProviderManifest } from '@/types/core/provider-manifest';
import type { Page, PageRequest } from '@/types/shared/page';
import { retryCommand } from '@/utils/retry';
import { type UUID, isUUID, newUUID } from '@/utils/uuid';

/**
 * Build a normalized name-to-logo map from provider manifests.
 * Normalizes both manifest names and provider names to handle variations.
 */
function buildLogoMapFromManifests(
  manifests: ProviderManifest[]
): Map<string, { light: string; dark: string }> {
  const logoMap = new Map<string, { light: string; dark: string }>();

  for (const manifest of manifests) {
    if (manifest.logo?.light) {
      // Map by display name
      if (manifest.displayName) {
        logoMap.set(manifest.displayName.toLowerCase(), manifest.logo);
      }
      // Map by name
      if (manifest.name) {
        logoMap.set(manifest.name.toLowerCase(), manifest.logo);
      }
    }
  }

  return logoMap;
}

async function fetchManifestLogoMap(
  tenantId: UUID
): Promise<Map<string, { light: string; dark: string }>> {
  try {
    const res = await api.listManifests(tenantId, { limit: 200 });
    const rawModels = extractPageModels<ManifestModel>(res);
    const manifests = rawModels
      .map(extractConnectorManifest)
      .filter((manifest): manifest is ProviderManifest => manifest !== null);
    return buildLogoMapFromManifests(manifests);
  } catch (error) {
    console.warn('Failed to load provider manifest logos:', error);
    return new Map<string, { light: string; dark: string }>();
  }
}

/**
 * Enrich providers with logos from the manifest catalog, where necessary.
 */
function enrichProvidersWithLogos(
  providers: Array<Provider>,
  logoMap: Map<string, { light: string; dark: string }>
): Array<Provider> {
  return providers.map((provider) => {
    // If provider already has a logo, keep it
    if (provider.logo?.light) {
      return provider;
    }

    // Look up logo by provider name (case-insensitive)
    const providerNameKey = provider.name?.toLowerCase();
    if (providerNameKey) {
      const logo = logoMap.get(providerNameKey);
      if (logo) {
        return { ...provider, logo };
      }
    }

    return provider;
  });
}

function buildApiQuery(query?: PageRequest): {
  limit?: number;
  next?: string;
  filter?: string;
} {
  return {
    limit: query?.limit,
    next: query?.next ?? undefined,
    filter:
      query?.filter === undefined
        ? undefined
        : typeof query.filter === 'string'
          ? query.filter
          : JSON.stringify(query.filter),
  };
}

export const providerService = {
  listProvidersRaw: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Provider>> => {
    const apiQuery = buildApiQuery(query);
    const res = await api.listProviders(tenantId, apiQuery);
    const rawModels = extractPageModels<ProviderModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: rawModels.map(extractProvider),
      next,
      prev,
      limit: query?.limit,
    };
  },

  listProviders: async (
    tenantId: UUID,
    query?: PageRequest
  ): Promise<Page<Provider>> => {
    const apiQuery = buildApiQuery(query);
    const res = await api.listProviders(tenantId, apiQuery);
    const rawModels = extractPageModels<ProviderModel>(res);
    const models = rawModels.map(extractProvider);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return { items: models, next, prev, limit: query?.limit };
  },

  findProvider: async (
    tenantId: UUID,
    providerId: UUID
  ): Promise<Provider | null> => {
    const base = await providerService.findProviderRaw(tenantId, providerId);
    if (!base) return null;
    const logoMap = await fetchManifestLogoMap(tenantId);
    const [enriched] = enrichProvidersWithLogos([base], logoMap);
    return enriched ?? null;
  },

  findProviderRaw: async (
    tenantId: UUID,
    providerId: UUID
  ): Promise<Provider | null> => {
    const res = await api.findProvider(tenantId, providerId);
    const model = unwrapData(res);
    return model ? extractProvider(model) : null;
  },

  createProvider: async (
    tenantId: UUID,
    provider: { name: string; description?: string }
  ): Promise<Provider> => {
    const providerId = newUUID();
    const res = await api.reconcileProvider(tenantId, providerId, {
      name: provider.name ?? '',
      description: provider.description ?? '',
      is_system: false,
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to create provider');
    }
    return { ...provider, id: providerId } as Provider;
  },

  updateProvider: async (
    tenantId: UUID,
    providerId: UUID,
    updates: { name?: string; description?: string }
  ): Promise<Provider> => {
    const existing = await providerService.findProvider(tenantId, providerId);
    if (!existing) {
      throw new Error('Provider not found');
    }
    const res = await api.reconcileProvider(tenantId, providerId, {
      name: updates.name ?? existing.name ?? '',
      description: updates.description ?? existing.description ?? '',
      is_system: existing.isSystem ?? false,
    });
    if (!extractAccepted(res)) {
      throw new Error('Failed to update provider');
    }
    return { ...existing, ...updates } as Provider;
  },

  deleteProvider: async (
    tenantId: UUID,
    providerId: UUID
  ): Promise<boolean> => {
    const res = await api.removeProvider(tenantId, providerId);
    return extractBoolean(res);
  },

  /** Find a specific provider feature by ID */
  findProviderFeature: async (
    tenantId: UUID,
    providerId: UUID,
    featureId: UUID
  ): Promise<Feature | null> => {
    const res = await api.findProviderFeature(tenantId, providerId, featureId);
    const model = unwrapData(res);
    return model ? extractProviderFeature(model) : null;
  },

  /** List all features for a specific provider */
  listProviderFeatures: async (
    tenantId: UUID,
    providerId: UUID,
    query?: PageRequest
  ): Promise<Page<Feature>> => {
    const apiQuery = buildApiQuery(query);
    const res = await api.listProviderFeatures(tenantId, providerId, apiQuery);
    const models = extractPageModels<FeatureModel>(res);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;
    return {
      items: models.map(extractProviderFeature),
      next,
      prev,
      limit: query?.limit,
    };
  },

  /** Create or update a provider feature (feature_id in route; use newUUID() for create) */
  reconcileProviderFeature: async (
    tenantId: UUID,
    providerId: UUID,
    featureId: UUID,
    body: {
      feature_name: string;
      description: string;
      options: string;
      payload_schema?: unknown;
      requirements: Array<string>;
      credentials?: Array<CredentialRequirement>;
    }
  ): Promise<boolean> => {
    const apiBody: {
      feature_name: string;
      description: string;
      options: string;
      payload_schema?: unknown;
      requirements: Array<string>;
      credentials?: Array<CredentialRequirement>;
    } = {
      feature_name: body.feature_name,
      description: body.description,
      options: body.options,
      payload_schema: body.payload_schema,
      requirements: body.requirements,
    };
    // Only include credentials when at least one slot is supplied; omit otherwise
    // so the backend treats the feature as requiring no secret.
    if (body.credentials && body.credentials.length > 0) {
      apiBody.credentials = body.credentials;
    }
    const res = await api.reconcileProviderFeature(
      tenantId,
      providerId,
      featureId,
      apiBody
    );
    return extractAccepted(res);
  },

  /** Remove a provider feature */
  removeProviderFeature: async (
    tenantId: UUID,
    providerId: UUID,
    featureId: UUID
  ): Promise<boolean> => {
    const res = await api.removeProviderFeature(
      tenantId,
      providerId,
      featureId
    );
    return extractBoolean(res);
  },

  /** Enable a provider */
  enableProvider: async (
    tenantId: UUID,
    providerId: UUID
  ): Promise<boolean> => {
    const res = await api.changeProviderStatus(tenantId, providerId, {
      status: 'enabled',
    });
    return extractBoolean(res);
  },

  /** Disable a provider */
  disableProvider: async (
    tenantId: UUID,
    providerId: UUID
  ): Promise<boolean> => {
    const res = await api.changeProviderStatus(tenantId, providerId, {
      status: 'disabled',
    });
    return extractBoolean(res);
  },

  listAvailableProviders: async (
    tenantId: UUID,
    query?: Pick<PageRequest, 'limit' | 'next'>
  ): Promise<Page<ProviderManifest>> => {
    const res = await api.listManifests(tenantId, {
      limit: query?.limit,
      next: query?.next ?? undefined,
    });
    const rawModels = extractPageModels<ManifestModel>(res);
    const items = rawModels
      .map(extractConnectorManifest)
      .filter((manifest): manifest is ProviderManifest => manifest !== null);
    const next = res?.data?.next ?? null;
    const prev = res?.data?.prev ?? null;

    return {
      items,
      next,
      prev,
      limit: query?.limit,
    };
  },

  importProviderFromManifest: async (
    tenantId: UUID,
    providerTemplate: ProviderManifest
  ): Promise<UUID> => {
    const manifestId = providerTemplate.providerId;
    const manifestVersion = providerTemplate.version;

    if (!manifestId || !isUUID(manifestId)) {
      throw new Error(
        'Provider template is missing a valid manifest provider id'
      );
    }

    if (!manifestVersion) {
      throw new Error('Provider template is missing a manifest version');
    }

    // Importing a manifest is keyed by manifestId, so retrying a transient
    // non-accepted response is idempotent — the user no longer has to refresh
    // and resubmit when the first attempt races ahead of the backend.
    await retryCommand(async () => {
      const res = await api.importManifest(tenantId, manifestId, {
        version: manifestVersion,
      });
      if (!extractAccepted(res)) {
        throw new Error('Failed to import provider from manifest');
      }
    });

    return manifestId;
  },
};
